import './purchaseDrawer.scss';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Drawer } from '@eliberty/design-system-react/mantine';

import { NextStepButton, Stepper } from '~atoms';
import { PURCHASE_STEP } from '~constants';
import { findModalPortal, shouldShowNoOptionSelectedMessage } from '~helpers';
import { useDeferredOpen } from '~hooks';
import {
  CartTitle,
  FooterActionBar,
  HeaderActionBar,
  ProductCategoryInformation,
  PromotionAlerts,
  UpsellProposals,
} from '~molecules';
import { CartView, OptionsSelector } from '~organisms';
import { ConsumerCategoryConfirmationState } from '~types/participant';

import { ProductSelectors } from '../Configurator/productSelectors';

import { PurchaseDrawerProps } from './purchaseDrawerProps';

type PurchaseStepItem = { id: string; label: ReactNode };

const configurationStep: PurchaseStepItem = {
  id: 'configuration',
  label: <FormattedMessage defaultMessage='CONFIGURATION' id='purchaseStepConfiguration' />,
};
const optionsStep: PurchaseStepItem = {
  id: 'options',
  label: <FormattedMessage defaultMessage='OPTIONS' id='purchaseStepOptions' />,
};
const cartStep: PurchaseStepItem = {
  id: 'cart',
  label: <FormattedMessage defaultMessage='CART' id='cartStepLabel' />,
};

const buildPurchaseSteps = (cartEnabled: boolean): PurchaseStepItem[] =>
  cartEnabled
    ? [configurationStep, optionsStep, cartStep]
    : [configurationStep, optionsStep];

const getPurchaseStepIndex = (step: string): number => {
  if (step === PURCHASE_STEP.CART) return 2;
  if (step === PURCHASE_STEP.OPTIONS) return 1;
  return 0;
};

const withModifier = (base: string, modifier: string, active: boolean): string =>
  active ? `${base} ${modifier}` : base;

interface DesktopTitleProps {
  isCartStep: boolean;
  showBackButton: boolean;
  cartParticipantCount: number;
  purchaseStepIndex: number;
  stepperSteps: PurchaseStepItem[];
  onBackClick: () => void;
  onStepClick: (index: number) => void;
  backLabel: string;
}

const DesktopTitle = ({
  backLabel,
  cartParticipantCount,
  isCartStep,
  onBackClick,
  onStepClick,
  purchaseStepIndex,
  showBackButton,
  stepperSteps,
}: DesktopTitleProps) => (
  <>
    <div className='PurchaseDrawer_titleRow'>
      {showBackButton && (
        <button
          type='button'
          className='PurchaseDrawer_backButton'
          onClick={onBackClick}
          aria-label={backLabel}
        >
          <i className='elibertyIcon-chevronLeft' aria-hidden='true' />
        </button>
      )}
      <span className='PurchaseDrawer_title'>
        {isCartStep ? (
          <CartTitle
            countClassName='PurchaseDrawer_titleCount'
            participantCount={cartParticipantCount}
          />
        ) : (
          <FormattedMessage id='purchaseDrawerTitle' defaultMessage='Your selection' />
        )}
      </span>
    </div>
    <Stepper
      active={purchaseStepIndex}
      size='sm'
      onStepClick={onStepClick}
      steps={stepperSteps}
    />
  </>
);

/**
 * PurchaseDrawer — Drawer template for the purchase widget.
 *
 * Renders a right-side Drawer (Mantine) containing:
 * - Step PRODUCT: category/validity/date/participant selectors in accordion mode
 * - Step OPTIONS: options selector
 * - Footer: price summary + CTA button (add to cart / continue)
 *
 * This single template replaces the desktop/mobile split of previous widgets.
 */
const PurchaseDrawer = ({
  isOpen,
  onClose,
  purchaseStep,
  productCategory,
  filteredValidities,
  date,
  selectedTimeslot,
  validityCategory,
  product,
  products,
  participants,
  simulation,
  participantsLimit,
  isSimulationOnError,
  consumerCategories,
  participantMode,
  isLoadingPrices = false,
  isLoadingProductCategory = false,
  onChangeValidityCategory,
  onChangeDate,
  onChangeParticipants,
  onClickAddToCart,
  isDateDisabled,
  isParticipantsDisabled,
  onClickAddOptions,
  onClickBackButton,
  onTimeslotSelected,
  productCategories,
  selectedProductCategoryShortname,
  onProductCategoryChange,
  isLoadingProductCategories = false,
  hasProductCategoriesError = false,
  hasProductCategoryError = false,
  onExitTransitionEnd,
  fullScreen = false,
  mobileHeaderProps,
  availableUpsellings = [],
  onUpsellSelectionChange,
  selectedUpsellProposal,
  shopUrl,
  selectedContractor,
  onCartCheckout,
  cartParticipantCount = 0,
  cartEnabled = false,
}: PurchaseDrawerProps) => {
  const intl = useIntl();

  const deferredOpen = useDeferredOpen(isOpen);

  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const { orderItems, currency, prices, promotions } = simulation || {};
  const basePrice = prices?.publicTotal;
  const finalPrice = prices?.total;

  const isCartStep = cartEnabled && purchaseStep === PURCHASE_STEP.CART;

  // UPSELL intercepts between PRODUCT and OPTIONS but does not change the stepper indicator
  const purchaseStepIndex = getPurchaseStepIndex(purchaseStep);

  const purchaseSteps = buildPurchaseSteps(cartEnabled);
  const stepperSteps = isCartStep
    ? purchaseSteps.map((s) => ({ ...s, allowStepSelect: false }))
    : purchaseSteps;

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      if (isCartStep) {
        return;
      }
      if (
        stepIndex === 0 &&
        (purchaseStep === PURCHASE_STEP.OPTIONS || purchaseStep === PURCHASE_STEP.UPSELL)
      ) {
        onClickBackButton();
      }
    },
    [isCartStep, onClickBackButton, purchaseStep],
  );

  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(false);
  }, [purchaseStep]);
  const [consumerCategoryConfirmation, setConsumerCategoryConfirmation] =
    useState<ConsumerCategoryConfirmationState | null>(null);
  const [hasDynamicDiscount, setHasDynamicDiscount] = useState(false);
  const [isFloatingPromotionVisible, setIsFloatingPromotionVisible] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const hasPromotions = hasDynamicDiscount || !!promotions?.length;
  const showFloatingPromotions = hasPromotions;

  const shouldDisplayNoOptionSelectedMessage = shouldShowNoOptionSelectedMessage({
    configurationStep: purchaseStep,
    participants,
    simulation,
  });

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      setLoading(true);
      onClickAddToCart(e);
    },
    [onClickAddToCart],
  );

  // Floating promotions: show briefly then auto-hide (same as ConfiguratorMobile)
  useEffect(() => {
    if (!showFloatingPromotions) {
      setIsFloatingPromotionVisible(false);
      return;
    }

    setIsFloatingPromotionVisible(true);

    if (typeof globalThis === 'undefined') {
      return;
    }

    const hideFloatingPromotions = () => setIsFloatingPromotionVisible(false);
    const timeoutId = globalThis.setTimeout(hideFloatingPromotions, 5000);
    const scrollTarget = bodyRef.current;
    scrollTarget?.addEventListener('scroll', hideFloatingPromotions, { passive: true });

    return () => {
      globalThis.clearTimeout(timeoutId);
      scrollTarget?.removeEventListener('scroll', hideFloatingPromotions);
    };
  }, [showFloatingPromotions, simulation]);

  const showBackButton = purchaseStep !== PURCHASE_STEP.PRODUCT && !isCartStep;

  const drawerTitle = mobileHeaderProps ? (
    <HeaderActionBar
      title={mobileHeaderProps.title || ''}
      subTitle={intl.formatMessage({ defaultMessage: 'View details', id: 'viewDetails' })}
      onBackClick={mobileHeaderProps.onBackClick}
      onDetailsClick={() => setIsDetailsDrawerOpen(true)}
    />
  ) : (
    <DesktopTitle
      backLabel={intl.formatMessage({ defaultMessage: 'Back', id: 'back' })}
      cartParticipantCount={cartParticipantCount}
      isCartStep={isCartStep}
      onBackClick={onClickBackButton}
      onStepClick={handleStepClick}
      purchaseStepIndex={purchaseStepIndex}
      showBackButton={showBackButton}
      stepperSteps={stepperSteps}
    />
  );

  return (
    <Drawer
      opened={deferredOpen}
      onClose={fullScreen ? () => {} : onClose}
      position='right'
      size={fullScreen ? '100%' : 'lg'}
      withCloseButton={!fullScreen}
      closeOnClickOutside={!fullScreen}
      closeOnEscape={!fullScreen}
      withOverlay={!fullScreen}
      portalProps={{ target: findModalPortal() }}
      onExitTransitionEnd={onExitTransitionEnd}
      classNames={{
        body: withModifier('PurchaseDrawer_drawerBody', 'PurchaseDrawer_drawerBody--cart', isCartStep),
        content: withModifier('PurchaseDrawer_content', 'PurchaseDrawer_content--fullScreen', fullScreen),
        header: withModifier('PurchaseDrawer_header', 'PurchaseDrawer_header--mobile', !!mobileHeaderProps),
        title: mobileHeaderProps
          ? 'PurchaseDrawer_mobileHeaderContent'
          : 'PurchaseDrawer_headerContent',
      }}
      title={drawerTitle}
      data-testid='purchase-drawer'
    >
      <Drawer.Body>
        <div
          className={withModifier('PurchaseDrawer_body', 'PurchaseDrawer_body--cart', isCartStep)}
          ref={bodyRef}
        >
          {mobileHeaderProps && (
            <Stepper
              active={purchaseStepIndex}
              size='sm'
              onStepClick={handleStepClick}
              steps={stepperSteps}
            />
          )}
          {purchaseStep === PURCHASE_STEP.PRODUCT && (
            <ProductSelectors
              productCategory={productCategory}
              filteredValidities={filteredValidities}
              date={date}
              selectedTimeslot={selectedTimeslot}
              validityCategory={validityCategory}
              product={product}
              products={products}
              participants={participants}
              orderItems={orderItems}
              currency={currency}
              participantsLimit={participantsLimit}
              isSimulationOnError={isSimulationOnError}
              consumerCategories={consumerCategories}
              participantMode={participantMode}
              isLoadingPrices={isLoadingPrices}
              isLoadingProductCategory={isLoadingProductCategory}
              isDateDisabled={isDateDisabled}
              isParticipantsDisabled={isParticipantsDisabled}
              onChangeValidityCategory={onChangeValidityCategory}
              onChangeDate={onChangeDate}
              onChangeParticipants={onChangeParticipants}
              onTimeslotSelected={onTimeslotSelected}
              onConsumerCategoryConfirmationChange={setConsumerCategoryConfirmation}
              onDynamicDiscountChange={setHasDynamicDiscount}
              productCategories={productCategories}
              selectedProductCategoryShortname={selectedProductCategoryShortname}
              onProductCategoryChange={onProductCategoryChange}
              isLoadingProductCategories={isLoadingProductCategories}
              hasProductCategoriesError={hasProductCategoriesError}
              hasProductCategoryError={hasProductCategoryError}
              selectedContractor={selectedContractor}
            />
          )}
          {purchaseStep === PURCHASE_STEP.UPSELL && availableUpsellings.length > 0 && (
            <UpsellProposals
              proposals={availableUpsellings}
              currency={currency}
              date={date}
              participants={participants}
              shopUrl={shopUrl}
              selectedProduct={
                productCategory
                  ? {
                      image: productCategory.additionalInfo?.image ?? null,
                      name: productCategory.name,
                      validityCategoryValue: validityCategory?.value,
                    }
                  : undefined
              }
              onSelectionChange={onUpsellSelectionChange}
            />
          )}
          {currency && purchaseStep === PURCHASE_STEP.OPTIONS && (
            <OptionsSelector
              date={date}
              options={simulation?.availableOptions ?? []}
              participants={participants}
              currency={currency}
              participantMode={participantMode}
              onChangeParticipants={onChangeParticipants}
              onClickBackButton={onClickBackButton}
            />
          )}
          {isCartStep && <CartView onCheckout={onCartCheckout ?? (() => {})} />}
        </div>
      </Drawer.Body>

      {showFloatingPromotions && isFloatingPromotionVisible && (
        <div className='PurchaseDrawer_floatingPromotions' data-testid='floatingPromotions'>
          <PromotionAlerts
            promotions={promotions}
            className='PromotionAlerts--floating'
            showDescriptions={false}
            hasDynamicDiscount={hasDynamicDiscount}
          />
        </div>
      )}

      {!isCartStep && (
        <FooterActionBar
          finalPrice={finalPrice}
          currency={currency}
          validityCategory={validityCategory}
          date={date}
          nextStepButton={
            <NextStepButton
              simulation={simulation}
              configurationStep={purchaseStep}
              participants={participants}
              loading={loading || isLoadingPrices}
              isSimulationOnError={isSimulationOnError}
              finalPrice={finalPrice}
              onClickAddToCart={handleAddToCart}
              onClickAddOptions={onClickAddOptions}
              consumerCategoryConfirmationState={consumerCategoryConfirmation}
              hasSelectedUpsell={!!selectedUpsellProposal}
            />
          }
          isSimulationOnError={isSimulationOnError}
          basePrice={basePrice}
          promotions={promotions}
          participants={participants}
          orderItems={orderItems}
          selectedProduct={product}
          participantMode={participantMode}
          isLoading={isLoadingPrices}
          simulation={simulation}
          renderInlinePromotionAlerts={!showFloatingPromotions}
          hasDynamicDiscount={hasDynamicDiscount}
          priceHelper={
            shouldDisplayNoOptionSelectedMessage ? (
              <FormattedMessage defaultMessage='No option selected' id='noOptionSelected' />
            ) : undefined
          }
        />
      )}
      {mobileHeaderProps && (
        <Drawer
          opened={isDetailsDrawerOpen}
          onClose={() => setIsDetailsDrawerOpen(false)}
          position='bottom'
          size='90%'
          classNames={{
            content: 'PurchaseDrawer_detailsDrawer',
            title: 'PurchaseDrawer_detailsDrawerTitle',
          }}
          portalProps={{ target: findModalPortal() }}
          title={intl.formatMessage({
            defaultMessage: 'Domain information',
            id: 'domainInformation',
          })}
        >
          <Drawer.Body>
            <div data-testid='detailsModalContent'>
              <ProductCategoryInformation
                title={productCategory?.name}
                additionalInfo={productCategory?.additionalInfo}
                isLoading={isLoadingProductCategory}
              />
            </div>
          </Drawer.Body>
        </Drawer>
      )}
    </Drawer>
  );
};

export default PurchaseDrawer;
